/**
 * SecureAuthProvider
 *
 * Provides secure authentication context to the application.
 */

'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { tokenService } from '../services/TokenService';
import { secureAuthService } from '../services/SecureAuthService';
import { authStateManager } from '../services/AuthStateManager';
import { setUserIdMapping } from '../services/userIdentity';
import { extractErrorMessage } from '@/shared/utils/error';
import type { LoginCredentials, AuthResult } from '../services/SecureAuthService';
import type { AuthState } from '../services/AuthStateManager';
import { authLogger } from '@/core/utils/logger';
import { setStorageItem, removeStorageItem, STORAGE_KEYS } from '@/core/utils/local-storage';
import { getErrorMessage } from '@voley/shared-utils';
import { config } from '@/config';
import { BackgroundSessionValidator } from '../services/BackgroundSessionValidator';
import { postLoginRedirect } from '../utils/postLoginRedirect';
import { useTokenEventSubscription } from '../hooks/useTokenEventSubscription';
import { useCrossTabAuthSync } from '../hooks/useCrossTabAuthSync';

import type { Schema } from '@voley/shared-types';

type User = Pick<Schema<'UserResponse'>, 'id' | 'email' | 'firstName' | 'lastName' | 'organizationId'>;

// Type guard to validate user object at runtime
function isValidUser(obj: unknown): obj is User {
  if (typeof obj !== 'object' || obj === null) return false;
  const candidate = obj as Record<string, unknown>;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.email === 'string' &&
    (typeof candidate.firstName === 'string' || candidate.firstName === undefined || candidate.firstName === null) &&
    (typeof candidate.lastName === 'string' || candidate.lastName === undefined || candidate.lastName === null) &&
    (typeof candidate.organizationId === 'string' || candidate.organizationId === undefined || candidate.organizationId === null)
  );
}

interface SecureAuthContextValue {
  // State
  isAuthenticated: boolean;
  isLoading: boolean;
  isLoggingOut: boolean;
  user: User | null;

  // Methods
  login: (credentials: LoginCredentials) => Promise<AuthResult>;
  loginWithSession: (user: User, sessionToken: string) => Promise<AuthResult>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  getToken: () => string | null;

  // Token info
  tokenStatus: ReturnType<typeof tokenService.getStatus>;
}

export const SecureAuthContext = createContext<SecureAuthContextValue | null>(null);

export function useSecureAuthContext() {
  const context = useContext(SecureAuthContext);
  if (!context) {
    // In development, warn but don't crash
    if (process.env.NODE_ENV === 'development') {
      authLogger.warn('[useSecureAuthContext] Used outside SecureAuthProvider - returning safe defaults');
    }
    
    // Return safe defaults when provider is not available
    return {
      isAuthenticated: false,
      isLoading: false,
      isLoggingOut: false,
      user: null,
      login: async () => ({ success: false, error: 'Provider not available' }),
      loginWithSession: async () => ({ success: false, error: 'Provider not available' }),
      logout: async () => {},
      refreshAuth: async () => {},
      getToken: () => null,
      tokenStatus: { hasToken: false, isExpired: true, expiresAt: null }
    };
  }
  return context;
}

interface SecureAuthProviderProps {
  children: React.ReactNode;
  lightweight?: boolean;
}

export function SecureAuthProvider({ children, lightweight = false }: SecureAuthProviderProps) {
  const router = useRouter();

  // SSR safety - avoid calling services during prerendering
  const isSSR = typeof window === 'undefined';
  const [isLoading, setIsLoading] = useState(!isSSR);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [_authState, setAuthState] = useState<AuthState>(
    isSSR
      ? {
          isAuthenticated: false,
          isRefreshing: false,
          user: null,
          session: {
            expiresAt: null,
            isExpiring: false,
            timeUntilExpiry: null,
          },
          lastActivity: 0, // Use 0 during SSR to avoid Date.now() hydration mismatch
          lastRefresh: null,
        }
      : authStateManager.getState()
  );
  const [tokenStatus, setTokenStatus] = useState(
    isSSR
      ? { hasToken: false, isExpired: false, isExpiring: false, timeUntilExpiry: null, hasRefreshInProgress: false }
      : tokenService.getStatus()
  );
  // Track current user explicitly; initialize from auth state when available
  const [user, setUser] = useState<User | null>(() => {
    if (isSSR) return null;
    const rawUser = authStateManager.getState().user;
    return isValidUser(rawUser) ? rawUser : null;
  });
  
  useEffect(() => {
    const handleStateChange = (newState: AuthState) => {
      setAuthState(newState);
      setTokenStatus(tokenService.getStatus());
      // Keep local user state in sync with global auth state
      try {
        const rawUser = newState.user;
        setUser(isValidUser(rawUser) ? rawUser : null);
      } catch (e) {
        authLogger.debug('[SecureAuthProvider] Error syncing user state:', e);
      }
    };
    
    authStateManager.on('state:changed', handleStateChange);
    
    return () => {
      authStateManager.off('state:changed', handleStateChange);
    };
  }, []);
  
  const updateTokenStatus = useCallback(() => {
    setTokenStatus(tokenService.getStatus());
  }, []);

  const login = useCallback(async (credentials: LoginCredentials): Promise<AuthResult> => {
    try {
      setIsLoading(true);
      const result = await secureAuthService.login(credentials);
      
      if (result.success && result.user) {
        authStateManager.setAuthenticatedUser({
          id: result.user.id,
          email: result.user.email,
          firstName: result.user.firstName,
          lastName: result.user.lastName,
          organizationId: result.user.organizationId,
        });
        updateTokenStatus();
        try {
          // Set user ID mapping early post-login
          const stytchId = (result.user as unknown as { stytchUserId?: string }).stytchUserId;
          if (stytchId && result.user.id) {
            setUserIdMapping(stytchId, result.user.id);
          }
        } catch (e) {
          authLogger.debug('[SecureAuthProvider] Error setting user ID mapping:', e);
        }

        // Cache auth state for faster subsequent loads
        if (typeof window !== 'undefined') {
          sessionStorage.setItem('auth_state_cache', JSON.stringify({
            user: result.user,
            timestamp: Date.now()
          }));
        }

        // Wait for CSRF cookie readiness, then redirect
        await postLoginRedirect();
      }

      return result;
    } catch (error) {
      authLogger.error('[SecureAuthProvider] Login error:', error);
      return {
        success: false,
        error: getErrorMessage(error),
      };
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router, updateTokenStatus]);

  // Login with session handler (for magic link flows)
  const loginWithSession = useCallback(async (userData: User): Promise<AuthResult> => {
    try {
      setIsLoading(true);
      
      // Set user data immediately since we already have it
      setUser(userData);

      // Don't store the actual session token in memory - backend uses httpOnly cookies
      tokenService.setTokens({
        accessToken: null, // No token in memory - use cookies only
        expiresIn: 3600, // 1 hour
        tokenType: 'Bearer',
        isCookieAuth: true, // Explicit flag for cookie-based Stytch auth
      });
      
      updateTokenStatus();

      // Attempt to set user ID mapping immediately from profile
      try {
        const profileResp = await fetch(`${config.api.baseUrl}/users/me/profile`, {
          headers: secureAuthService.attachAuthHeaders({}).headers,
          credentials: 'include'
        });
        if (profileResp.ok) {
          const profileData = await profileResp.json();
          const stytchId = profileData?.data?.stytchUserId;
          const apiId = profileData?.data?.id;
          if (stytchId && apiId) {
            setUserIdMapping(stytchId, apiId);
          }
        }
      } catch (e) {
        authLogger.debug('[SecureAuthProvider] Error fetching profile for ID mapping:', e);
      }

      // Wait for CSRF cookie readiness, then redirect
      await postLoginRedirect();

      return {
        success: true,
        user: userData,
      };
    } catch (error) {
      authLogger.error('[SecureAuthProvider] Session login error:', error);
      return {
        success: false,
        error: getErrorMessage(error),
      };
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router, updateTokenStatus]);

  const logout = useCallback(async () => {
    // Set logging out state IMMEDIATELY to show overlay before any state clearing
    // This prevents the visual flash where data disappears but dashboard is still visible
    setIsLoggingOut(true);

    try {
      setIsLoading(true);
      await secureAuthService.logout();
      setUser(null);
      updateTokenStatus();

      // Clear all auth-related session storage
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('auth_state_cache');
        sessionStorage.removeItem('auth_initial_check');

        // Broadcast logout to other tabs via localStorage storage event.
        // The 'storage' event fires in OTHER tabs (not the originating tab),
        // which is exactly the behavior we need for cross-tab sync.
        setStorageItem(STORAGE_KEYS.AUTH_LOGOUT, String(Date.now()));
        removeStorageItem(STORAGE_KEYS.AUTH_LOGOUT);
      }

      router.push('/login');
    } catch (error) {
      authLogger.error('[SecureAuthProvider] Logout error:', error);
      // Even if logout fails, clear local state
      setUser(null);
      updateTokenStatus();
      router.push('/login');
    } finally {
      setIsLoading(false);
      // Note: Don't reset isLoggingOut here - we want the overlay to stay
      // until navigation completes. It will be reset on next mount.
    }
  }, [router, updateTokenStatus]);
  
  const refreshAuth = useCallback(async () => {
    try {
      await secureAuthService.refreshAccessToken();
      updateTokenStatus();
    } catch (error) {
      authLogger.error('[SecureAuthProvider] Refresh error:', error);
      // If refresh fails, logout
      await logout();
    }
  }, [logout, updateTokenStatus]);
  
  // Initialize auth on mount
  useEffect(() => {
    // Skip initialization during SSR/prerendering
    if (typeof window === 'undefined') {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- SSR guard, synchronous bailout
      setIsLoading(false);
      return;
    }

    // Use an object to track mounted state - object reference is stable across async operations
    // This prevents stale closure issues where async callbacks capture old primitive values
    const mountedRef = { current: true };
    // AbortController for cancelling background fetches on unmount
    const abortController = new AbortController();
    // Background session validator — handles retry, cooldown, and stale detection
    const bgValidator = new BackgroundSessionValidator({
      validateSession: () => secureAuthService.validateSession(),
      onSessionInvalid: () => {
        if (!mountedRef.current) return;
        if (process.env.NODE_ENV === 'development') {
          authLogger.error('[AUTH_TOKEN_CLEAR] Clearing tokens due to background validation failure');
        }
        tokenService.clearTokens();
        setUser(null);
        sessionStorage.removeItem('auth_state_cache');
      },
    });

    const initAuth = async () => {

      // ALWAYS log auth init for debugging login issues
      const debugState = {
        hasToken: tokenService.getStatus().hasToken,
        hasCachedAuth: !!sessionStorage.getItem('auth_state_cache'),
        hasGraceCookie: document.cookie.includes('auth_valid_grace=1'),
        hasE2EMockAuth: document.cookie.includes('e2e_mock_auth=1'),
        hasJwtAccess: document.cookie.includes('jwt_access='),
        hasJwtRefresh: document.cookie.includes('jwt_refresh='),
        hasStytchSession: document.cookie.includes('stytch_session='),
        hasCsrfCookie: document.cookie.includes('csrf-token='),
        cookieCount: document.cookie.split(';').filter(c => c.trim()).length,
        path: window.location.pathname,
        lightweight,
        timestamp: new Date().toISOString(),
      };
      if (process.env.NODE_ENV === 'development') {
        authLogger.debug('[AUTH_DEBUG] initAuth START', debugState);
      }
      authLogger.info('[SecureAuthProvider] initAuth START', debugState);

      try {
        // E2E MOCK AUTH BYPASS: When running E2E tests with mock auth, trust the cookies
        // and mock user data without validation. This is gated by e2e_mock_auth cookie
        // which is only set by /api/e2e/login in non-production environments.
        const isE2EMockAuth = document.cookie.includes('e2e_mock_auth=1');
        if (isE2EMockAuth) {
          // Default mock user for E2E - used when sessionStorage isn't available yet
          const DEFAULT_E2E_MOCK_USER = {
            id: 'e2e-user-default',
            email: 'e2e-test@voley.test',
            firstName: 'E2E',
            lastName: 'Test User',
            organizationId: 'e2e-org-default',
            stytchUserId: 'e2e-stytch-user-default',
          };

          // Try to get mock user from sessionStorage, fall back to default
          let mockUser = DEFAULT_E2E_MOCK_USER;
          const e2eMockUser = sessionStorage.getItem('e2e_mock_user');
          if (e2eMockUser) {
            try {
              mockUser = JSON.parse(e2eMockUser);
            } catch (e) {
              authLogger.warn('[SecureAuthProvider] Failed to parse E2E mock user, using default:', e);
            }
          } else {
            // Also check auth_state_cache
            const authCache = sessionStorage.getItem('auth_state_cache');
            if (authCache) {
              try {
                const cached = JSON.parse(authCache);
                if (cached?.user) {
                  mockUser = cached.user;
                }
              } catch (e) {
                authLogger.warn('[SecureAuthProvider] Failed to parse auth cache in E2E mode:', e);
              }
            }
          }

          authLogger.info('[SecureAuthProvider] E2E mock auth detected, using mock user');
          setUser(mockUser);
          tokenService.setTokens({
            accessToken: null,
            expiresIn: 3600,
            tokenType: 'Bearer',
            isCookieAuth: true,
          });
          updateTokenStatus();
          setIsLoading(false);
          return; // Skip all validation - E2E mode
        }

        // CRITICAL: Skip validation if on auth pages without tokens
        // This prevents 401 floods when users land on login/signup pages
        if (typeof window !== 'undefined') {
          const currentPath = window.location.pathname;
          const authPages = ['/login', '/signup', '/get-started', '/reset-password', '/forgot-password'];
          const isOnAuthPage = authPages.some(page => currentPath === page || currentPath.startsWith(page + '/'));

          if (isOnAuthPage) {
            const hasToken = tokenService.getStatus().hasToken;
            if (!hasToken) {
              // On auth page without token — clear any stale cached state
              // that might linger after a deployment or session expiry to prevent
              // loops where stale sessionStorage causes brief false-authenticated states.
              try {
                sessionStorage.removeItem('auth_state_cache');
                sessionStorage.removeItem('auth_initial_check');
              } catch { /* ignore */ }
              authLogger.debug('[SecureAuthProvider] On auth page without token, cleared stale state');
              setIsLoading(false);
              return;
            }
          }
        }

        // In lightweight mode, just check for token existence
        if (lightweight) {
          const hasToken = tokenService.getStatus().hasToken;

          if (hasToken && typeof window !== 'undefined') {
            // Redirect authenticated users away from auth pages
            const currentPath = window.location.pathname;
            const authPages = ['/login', '/signup', '/get-started', '/reset-password', '/forgot-password'];

            if (authPages.some(page => currentPath === page || currentPath.startsWith(page + '/'))) {
              authLogger.info('[SecureAuthProvider] Authenticated user on auth page, redirecting');
              router.push('/dashboard');
            }
          }

          updateTokenStatus();
          setIsLoading(false);
          return;
        }

        // Check for validation grace cookie - if present, skip validation entirely
        const hasValidationGrace = document.cookie.split('; ').some(c => c.startsWith('auth_valid_grace=1'));

        // Check for cached auth state - use it optimistically
        const cachedAuth = sessionStorage.getItem('auth_state_cache');

        if (cachedAuth) {
          try {
            const cached = JSON.parse(cachedAuth);
            if (cached?.user) {
              // Use cached user immediately - don't block on validation
              setUser(cached.user);
              tokenService.setTokens({
                accessToken: null,
                expiresIn: 3600,
                tokenType: 'Bearer',
                isCookieAuth: true, // Required for cookie-based Stytch auth
              });
              updateTokenStatus();
              setIsLoading(false);

              // If we have grace cookie, skip validation entirely
              if (hasValidationGrace) {
                authLogger.debug('[SecureAuthProvider] Using cached auth with grace period');
                return;
              }

              // Validate session in BACKGROUND (non-blocking)
              // BackgroundSessionValidator handles retry, cooldown, and stale detection
              bgValidator.validate();

              // Fetch profile in background for ID mapping (non-blocking)
              fetch(`${config.api.baseUrl}/users/me/profile`, {
                headers: secureAuthService.attachAuthHeaders({}).headers,
                credentials: 'include',
                signal: abortController.signal
              }).then(async resp => {
                if (!mountedRef.current) return;
                if (resp.ok) {
                  const profileData = await resp.json();
                  const stytchId = profileData?.data?.stytchUserId;
                  const apiId = profileData?.data?.id;
                  if (stytchId && apiId) {
                    setUserIdMapping(stytchId, apiId);
                  }
                }
              }).catch((e) => {
                // Ignore abort errors - they're expected on unmount
                if (e instanceof Error && e.name === 'AbortError') return;
                authLogger.debug('[SecureAuthProvider] Error fetching profile in background:', e);
              });

              return;
            }
          } catch (e) {
            authLogger.warn('[SecureAuthProvider] Error parsing cached auth', e);
            sessionStorage.removeItem('auth_state_cache');
          }
        }

        // No cached auth - check if authenticated via cookies
        if (!mountedRef.current) return;

        const isAuthenticatedViaCookies = secureAuthService.isAuthenticated();
        if (process.env.NODE_ENV === 'development') {
          authLogger.debug('[AUTH_DEBUG] Cookie auth check', {
            isAuthenticatedViaCookies,
            hasValidationGrace,
            allCookies: document.cookie,
          });
        }

        if (isAuthenticatedViaCookies) {
          // If we have grace cookie, trust it without validation
          if (hasValidationGrace) {
            tokenService.setTokens({
              accessToken: null,
              expiresIn: 3600,
              tokenType: 'Bearer',
              isCookieAuth: true, // Required for cookie-based Stytch auth
            });
            updateTokenStatus();
            setIsLoading(false);

            // Fetch profile in background
            fetch(`${config.api.baseUrl}/users/me/profile`, {
              headers: secureAuthService.attachAuthHeaders({}).headers,
              credentials: 'include',
              signal: abortController.signal
            }).then(async resp => {
              if (!mountedRef.current) return;
              if (resp.ok) {
                const profileData = await resp.json();
                // Validate required fields before constructing user
                if (!profileData?.data?.id || !profileData?.data?.email) {
                  authLogger.warn('[SecureAuthProvider] Invalid profile response: missing id or email');
                  return;
                }
                const userData = {
                  id: profileData.data.id,
                  email: profileData.data.email,
                  firstName: profileData.data.firstName ?? '',
                  lastName: profileData.data.lastName ?? '',
                  organizationId: profileData.data.organizationId ?? ''
                };
                setUser(userData);
                sessionStorage.setItem('auth_state_cache', JSON.stringify({
                  user: userData,
                  timestamp: Date.now()
                }));
                if (profileData.data.stytchUserId && profileData.data.id) {
                  setUserIdMapping(profileData.data.stytchUserId, profileData.data.id);
                }
              }
            }).catch((e) => {
              // Ignore abort errors - they're expected on unmount
              if (e instanceof Error && e.name === 'AbortError') return;
              // Log non-abort errors — silent swallowing hides real failures
              authLogger.error('[SecureAuthProvider] Grace cookie profile fetch failed', {
                error: extractErrorMessage(e),
              });
            });
            return;
          }

          // No grace cookie - validate session (but with timeout now)
          if (process.env.NODE_ENV === 'development') {
            authLogger.debug('[AUTH_DEBUG] Calling validateSession...');
          }
          const validateStart = performance.now();
          const isValid = await secureAuthService.validateSession();
          const validateDuration = Math.round(performance.now() - validateStart);
          if (process.env.NODE_ENV === 'development') {
            authLogger.debug('[AUTH_DEBUG] validateSession result', {
              isValid,
              durationMs: validateDuration,
            });
          }

          if (!mountedRef.current) return;

          if (!isValid) {
            if (process.env.NODE_ENV === 'development') {
              authLogger.debug('[AUTH_DEBUG] Session INVALID - clearing tokens');
            }
            tokenService.clearTokens();
            // Also clear cached auth to prevent stale cache from re-triggering
            // optimistic auth on the next page load (e.g. after deployment)
            try { sessionStorage.removeItem('auth_state_cache'); } catch { /* ignore */ }
            setIsLoading(false);
            return;
          }

          // Valid session - set tokens and fetch profile
          if (process.env.NODE_ENV === 'development') {
            authLogger.debug('[AUTH_DEBUG] Session VALID - setting tokens');
          }
          tokenService.setTokens({
            accessToken: null,
            expiresIn: 3600,
            tokenType: 'Bearer',
            isCookieAuth: true, // Required for cookie-based Stytch auth
          });
          updateTokenStatus();
          if (process.env.NODE_ENV === 'development') {
            const newTokenStatus = tokenService.getStatus();
            authLogger.debug('[AUTH_DEBUG] Token status after setTokens', newTokenStatus);
          }
          setIsLoading(false);

          // Fetch profile in background (non-blocking)
          fetch(`${config.api.baseUrl}/users/me/profile`, {
            headers: secureAuthService.attachAuthHeaders({}).headers,
            credentials: 'include',
            signal: abortController.signal
          }).then(async resp => {
            if (!mountedRef.current) return;
            if (resp.ok) {
              const profileData = await resp.json();
              // Validate required fields before constructing user
              if (!profileData?.data?.id || !profileData?.data?.email) {
                authLogger.warn('[SecureAuthProvider] Invalid profile response: missing id or email');
                return;
              }
              const userData = {
                id: profileData.data.id,
                email: profileData.data.email,
                firstName: profileData.data.firstName ?? '',
                lastName: profileData.data.lastName ?? '',
                organizationId: profileData.data.organizationId ?? ''
              };
              setUser(userData);
              sessionStorage.setItem('auth_state_cache', JSON.stringify({
                user: userData,
                timestamp: Date.now()
              }));
              if (profileData.data.stytchUserId && profileData.data.id) {
                setUserIdMapping(profileData.data.stytchUserId, profileData.data.id);
              }
            }
          }).catch(error => {
            // Ignore abort errors - they're expected on unmount
            if (error instanceof Error && error.name === 'AbortError') return;
            authLogger.error('[SecureAuthProvider] Failed to fetch user profile:', error);
          });
        }
      } catch (error) {
        authLogger.error('[SecureAuthProvider] Init error:', error);
      } finally {
        if (mountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    initAuth();

    return () => {
      mountedRef.current = false;
      // Cancel pending validation retries
      bgValidator.destroy();
      // Cancel any in-flight fetch requests
      abortController.abort();
    };
  }, [updateTokenStatus, lightweight, router]);
  
  // Stable callbacks for hook subscriptions
  const clearAuthUser = useCallback(() => setUser(null), []);
  const handleTokenAuthError = useCallback(() => { logout(); }, [logout]);

  // Subscribe to token lifecycle events (skip in lightweight mode)
  useTokenEventSubscription({
    onTokenUpdate: updateTokenStatus,
    onAuthCleared: clearAuthUser,
    onAuthError: handleTokenAuthError,
    enabled: !lightweight,
  });

  // Cross-tab auth sync: clear local state when another tab logs out
  const handleCrossTabLogout = useCallback(() => {
    tokenService.clearTokens();
    setUser(null);
    sessionStorage.removeItem('auth_state_cache');
    sessionStorage.removeItem('auth_initial_check');
    router.push('/login');
  }, [router]);

  useCrossTabAuthSync({ onLogoutDetected: handleCrossTabLogout, enabled: !lightweight });

  // No polling needed — tokenStatus is kept current via TokenService events above.

  // Stay "authenticated" while a refresh is in progress — the token is expired but
  // the refresh is expected to restore it. This prevents DashboardBootstrap from
  // redirecting to /login during the brief window between token expiry and refresh completion.
  const isAuthenticated = tokenStatus.hasToken && (!tokenStatus.isExpired || tokenStatus.hasRefreshInProgress);

  const value: SecureAuthContextValue = {
    isAuthenticated,
    isLoading,
    isLoggingOut,
    user,
    login,
    loginWithSession,
    logout,
    refreshAuth,
    getToken: () => tokenService.getAccessToken(),
    tokenStatus,
  };
  
  return (
    <SecureAuthContext.Provider value={value}>
      {children}
    </SecureAuthContext.Provider>
  );
}

// Hooks should be imported directly from their modules or from services/index.ts
// to avoid circular dependencies

